"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { useApp } from "../../contexts/AppContext"
import {
  BiShow,
  BiPencil,
  BiTrash,
  BiCopy,
  BiToggleLeft,
  BiToggleRight,
  BiCog,
  BiCheckSquare,
  BiSquare,
  BiX,
  BiBlock,
  BiData,
} from "react-icons/bi"

import FeuilleModal from "../modals/FeuilleModal"
import ConfirmAlert from "../alerts/ConfirmAlert"
import DropdownOption from "../ui/DropdownOption"


export default function FeuilleList({ feuilles, classeurId }) {
  const navigate = useNavigate()

  const { deleteFeuilleAPI, updateFeuilleAPI, addFeuilleAPI, getFeuilleLignes, getFeuilleDataAPI, loading } = useApp()

  // États pour les modals
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [feuillesData, setFeuillesData] = useState({})
  const [editingFeuilleId, setEditingFeuilleId] = useState(null)
  const [deletingFeuilleId, setDeletingFeuilleId] = useState(null)
  const [selectedFeuilles, setSelectedFeuilles] = useState([])
  const [selectAll, setSelectAll] = useState(false)


  useEffect(() => {
    const fetchData = async () => {
      const results = {}
      for (const feuille of feuilles) {
        try {
          const data = await getFeuilleDataAPI(feuille.code_feuille)
          results[feuille.code_feuille] = data
        } catch (err) {
          console.error("Erreur chargement données feuille", feuille.code_feuille, err)
          results[feuille.code_feuille] = []
        }
      }
      setFeuillesData(results)
    }

    if (feuilles.length > 0) fetchData()
  }, [feuilles])

  const handleDeleteFeuille = async () => {
    if (deletingFeuilleId) {
      try {
        await deleteFeuilleAPI(deletingFeuilleId)
      } catch (error) {
        console.error("Erreur lors de la suppression:", error)
      }
    }
  }

  const handleBulkDelete = async () => {
    try {
      for (const feuilleId of selectedFeuilles) {
        await deleteFeuilleAPI(feuilleId)
      }
      setSelectedFeuilles([])
      setSelectAll(false)
    } catch (error) {
      console.error("Erreur lors de la suppression en lot:", error)
    }
  }

  const handleEditFeuille = (feuilleId) => {
    setEditingFeuilleId(feuilleId)
    setShowEditModal(true)
  }

  const handleDuplicateFeuille = async (feuille) => {
    try {
      const duplicatedFeuille = {
        ...feuille,
        nom_feuille: `${feuille.nom_feuille} (Copie)`,
        libelle_feuille: `Copie de ${feuille.libelle_feuille || feuille.nom_feuille}`,
        date_insertion: new Date().toISOString(),
      }
      delete duplicatedFeuille.code_feuille
      await addFeuilleAPI(duplicatedFeuille)
    } catch (error) {
      console.error("Erreur lors de la duplication:", error)
    }
  }

  const handleToggleStatus = async (feuille) => {
    try {
      const updatedFeuille = {
        ...feuille,
        statut: feuille.statut === 1 ? 0 : 1,
      }
      await updateFeuilleAPI(feuille.code_feuille, updatedFeuille)
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error)
    }
  }

  const handleBulkStatusChange = async (newStatus) => {
    try {
      for (const feuilleId of selectedFeuilles) {
        const feuille = feuilles.find((f) => f.code_feuille === feuilleId)
        if (feuille) {
          const updatedFeuille = { ...feuille, statut: newStatus }
          await updateFeuilleAPI(feuilleId, updatedFeuille)
        }
      }
      setSelectedFeuilles([])
      setSelectAll(false)
    } catch (error) {
      console.error("Erreur lors du changement de statut en lot:", error)
    }
  }

  const handleSelectFeuille = (feuilleId) => {
    setSelectedFeuilles((prev) =>
      prev.includes(feuilleId) ? prev.filter((id) => id !== feuilleId) : [...prev, feuilleId],
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFeuilles([])
    } else {
      setSelectedFeuilles(feuilles.map((f) => f.code_feuille))
    }
    setSelectAll(!selectAll)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingFeuilleId(null)
  }

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false)
    setDeletingFeuilleId(null)
  }

  const handleCloseBulkDeleteConfirm = () => {
    setShowBulkDeleteConfirm(false)
  }

  // Variants d'animation
  const tableVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const rowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    hover: {
      backgroundColor: "rgba(0,0,0,0.02)",
      transition: { duration: 0.2 },
    },
  }

  return (
    <div className=" position-relative">
      {/* Actions en lot */}
      <AnimatePresence>
        {selectedFeuilles.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ duration: 0.1 }}
            exit={{ height: 0, opacity: 0 }}
            className="alert d-flex justify-content-between align-items-center mb-0 py-2 rounded-0"
            style={{ backgroundColor: "#e3f2fc", border: "none" }}
          >
            <span>
              <strong>{selectedFeuilles.length}</strong> feuille{selectedFeuilles.length > 1 ? "s" : ""} sélectionnée
              {selectedFeuilles.length > 1 ? "s" : ""}
            </span>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-success d-flex align-items-center" onClick={() => handleBulkStatusChange(1)} disabled={loading}>
                <BiCheckSquare className="me-1" /> Activer
              </button>
              <button className="btn btn-sm btn-warning d-flex align-items-center" onClick={() => handleBulkStatusChange(0)} disabled={loading}>
                <BiBlock className="me-1" /> Désactiver
              </button>
              <button className="btn btn-sm btn-danger d-flex align-items-center" onClick={() => setShowBulkDeleteConfirm(true)} disabled={loading}>
                <BiTrash className="me-1" /> Supprimer
              </button>
              <button className="btn btn-sm btn-outline-secondary d-flex align-items-center" onClick={() => {
                setSelectedFeuilles([])
                setSelectAll(false)
              }}>
                <BiX className="me-1" /> Annuler
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="table-responsive">
        <motion.table className="table table-hover table-bordered" variants={tableVariants} initial="hidden" animate="visible">
          <thead className="table-light">
            <tr>
              <th style={{ width: "40px" }}>
                <button
                  className="btn btn-sm p-0"
                  onClick={handleSelectAll}
                  title={selectAll ? "Désélectionner tout" : "Sélectionner tout"}>
                  {selectAll ? <BiCheckSquare /> : <BiSquare />}
                </button>
              </th>
              <th>Nom</th>
              <th>Description</th>
              <th>Statut</th>
              <th>Colonnes</th>
              <th>Données</th>
              <th>Date création</th>
              <th style={{ width: "120px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {feuilles.map((feuille) => {
                const feuilleData = feuillesData[feuille.code_feuille] || []

                return (
                  <motion.tr
                    key={feuille.code_feuille}
                    variants={rowVariants}
                    whileHover="hover"
                    layout
                  >
                    <td>
                      <button
                        className="btn btn-sm p-0"
                        onClick={() => handleSelectFeuille(feuille.code_feuille)}
                      >
                        {selectedFeuilles.includes(feuille.code_feuille) ? (
                          <BiCheckSquare className="text-primary" />
                        ) : (
                          <BiSquare />
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {feuille.icone && (
                          <i className={`bi bi-${feuille.icone} text-primary`}></i>
                        )}
                        <span className="fw-semibold">{feuille.nom_feuille}</span>
                      </div>
                    </td>
                    <td className="text-muted">
                      {feuille.libelle_feuille ? (
                        feuille.libelle_feuille.length > 50 ? (
                          <span title={feuille.libelle_feuille}>
                            {feuille.libelle_feuille.substring(0, 50)}...
                          </span>
                        ) : (
                          feuille.libelle_feuille
                        )
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span className={`badge ${feuille.statut === 1 ? "bg-success" : "bg-secondary" }`}>
                        {feuille.statut === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark"> {getFeuilleLignes(feuille.code_feuille).length || 0}</span>
                        <button onClick={() => navigate(`/feuille/${feuille.code_feuille}/fields`)}
                          className="btn btn-sm btn-outline-light text-secondary d-flex align-items-center justify-content-center rounded-pill p-1"
                          title="Configurer">
                          <BiCog className="fs-6" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="d-flex align-items-center gap-2">
                        <span className="badge bg-light text-dark">{feuilleData.length || 0}</span>
                        <button onClick={() => navigate(`/feuille/${feuille.code_feuille}/data`)}
                          className="btn btn-sm btn-outline-light text-secondary d-flex align-items-center justify-content-center rounded-pill p-1"
                          title="Configurer">
                          <BiData className="fs-6" />
                        </button>
                      </span>
                    </td>
                    <td className="text-muted small">
                      {feuille.date_insertion
                        ? new Date(feuille.date_insertion).toLocaleDateString("fr-FR")
                        : "-"}
                    </td>
                    <td>
                      <div className="d-flex gap-2 position-absolute">
                        <DropdownOption
                          options={[
                            {
                              label: "Modifier",
                              icon: <BiPencil />,
                              onClick: () => handleEditFeuille(feuille.code_feuille),
                              className: "text-primary",
                            },
                            {
                              label: "Dupliquer",
                              icon: <BiCopy />,
                              onClick: () => handleDuplicateFeuille(feuille),
                              className: "text-success",
                            },
                            {
                              label: feuille.statut === 1 ? "Désactiver" : "Activer",
                              icon:
                                feuille.statut === 1 ? <BiToggleLeft /> : <BiToggleRight />,
                              onClick: () => handleToggleStatus(feuille),
                              className:
                                feuille.statut === 1
                                  ? "text-warning"
                                  : "text-success",
                            },
                            {
                              label: "Supprimer",
                              icon: <BiTrash />,
                              onClick: () => {
                                setDeletingFeuilleId(feuille.code_feuille)
                                setShowDeleteConfirm(true)
                              },
                              className: "text-danger",
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </motion.tr>
                )
              })}
            </AnimatePresence>
          </tbody>

        </motion.table>
      </div>

      {/* Modals */}
      <FeuilleModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        classeurId={classeurId}
        feuilleId={editingFeuilleId}
      />

      <ConfirmAlert
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleDeleteFeuille}
        title="Supprimer la feuille"
        message={`Êtes-vous sûr de vouloir supprimer la feuille "${feuilles.find((f) => f.code_feuille === deletingFeuilleId)?.nom_feuille
          }" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />

      <ConfirmAlert
        isOpen={showBulkDeleteConfirm}
        onClose={handleCloseBulkDeleteConfirm}
        onConfirm={handleBulkDelete}
        title="Supprimer les feuilles sélectionnées"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedFeuilles.length} feuille${selectedFeuilles.length > 1 ? "s" : ""
          } ? Cette action est irréversible.`}
        confirmText="Supprimer tout"
        variant="danger"
      />
    </div>
  )
}
